import type {
  DraftVisibility,
  EventDraftResponse,
  EventMinAge,
  EventTicketUrlKind,
  GeneratedEventLocation,
} from '@pulsify/shared/dto'
import { guessTicketUrlKind } from '~/entities/event/lib/ticket-url-kind'
import type { TicketTypeInput } from '~/entities/ticket/api/tickets-api'
import { hasConfirmationStep } from '~/features/events/lib/address-visibility'
import type { EventEntry } from '~/features/events/lib/event-entry'

export type DetailQuestion = 'when' | 'place' | 'entry' | 'category'

/** Everything the chat settles about the event beyond its texts and pictures. */
export interface ChatDetails {
  startTime: string | null
  endTime: string | null
  location: GeneratedEventLocation | null
  listingId: string | null
  locationPending: boolean
  addressHidden: boolean
  entry: EventEntry | null
  ticketTypes: TicketTypeInput[]
  ticketUrl: string
  ticketUrlKind: EventTicketUrlKind
  capacity: number | null
  privacy: DraftVisibility
  liveRoom: boolean
  minAge: EventMinAge | null
  studentsOnly: boolean
  targetAgeMin: number | null
  targetAgeMax: number | null
  audienceNote: string
  mainCategoryId: string
  subCategoryTags: string[]
  amenityIds: string[]
  asked: DetailQuestion[]
}

const QUESTION_ORDER: DetailQuestion[] = ['when', 'place', 'entry', 'category']

function knownEntry(draft: EventDraftResponse): EventEntry | null {
  if (draft.ticketUrl?.trim()) return 'link'
  if (draft.signupRequired) return 'signup'
  if (draft.price.kind === 'free' && draft.ticketTypes.length === 0) return 'free'
  return null
}

export function detailsFromDraft(draft: EventDraftResponse): ChatDetails {
  const ticketUrl = draft.ticketUrl?.trim() ?? ''
  return {
    startTime: draft.startTime,
    endTime: draft.endTime,
    location: draft.location,
    listingId: draft.listingId,
    locationPending: draft.locationPending,
    addressHidden: draft.addressHidden,
    entry: knownEntry(draft),
    ticketTypes: draft.ticketTypes.map((type) => ({
      name: type.name,
      priceCents: type.priceCents,
      isStudent: type.isStudent,
      doorPriceCents: type.doorPriceCents,
      quantity: null,
    })),
    ticketUrl,
    ticketUrlKind: draft.ticketUrlKind ?? guessTicketUrlKind(ticketUrl, draft.signupRequired),
    capacity: draft.capacity,
    privacy: draft.visibility,
    liveRoom: true,
    minAge: draft.minAge,
    studentsOnly: draft.studentsOnly,
    targetAgeMin: draft.targetAgeMin,
    targetAgeMax: draft.targetAgeMax,
    audienceNote: draft.audienceNote ?? '',
    mainCategoryId: draft.tags.mainCategoryId,
    subCategoryTags: draft.tags.subCategoryTags,
    amenityIds: draft.tags.amenityIds,
    asked: [],
  }
}

export function placeKnown(details: ChatDetails): boolean {
  if (details.listingId || details.location?.coordinates) return true
  return details.locationPending && !!details.location?.city.trim()
}

function open(details: ChatDetails, question: DetailQuestion): boolean {
  switch (question) {
    case 'when':
      return !details.startTime
    case 'place':
      return !placeKnown(details)
    case 'entry':
      return details.entry === null
    case 'category':
      return !details.mainCategoryId || details.subCategoryTags.length === 0
  }
}

/** The next thing the chat still has to ask; nothing it already knows or already asked. */
export function nextQuestion(details: ChatDetails): DetailQuestion | null {
  return (
    QUESTION_ORDER.find(
      (question) => open(details, question) && !details.asked.includes(question),
    ) ?? null
  )
}

export function answerQuestion(
  details: ChatDetails,
  question: DetailQuestion,
  patch: Partial<ChatDetails> = {},
): ChatDetails {
  const asked = details.asked.includes(question) ? details.asked : [...details.asked, question]
  return { ...details, ...patch, asked }
}

/** Pulsify tickets for an event whose sources name no ticket categories: one from the price. */
export function ticketTypesForEntry(
  details: ChatDetails,
  price: EventDraftResponse['price'],
  name: string,
): TicketTypeInput[] {
  if (details.ticketTypes.length > 0) return details.ticketTypes
  const euros =
    price.kind === 'fixed'
      ? (price.priceFixed ?? 0)
      : price.kind === 'range'
        ? (price.priceRangeMin ?? 0)
        : 0
  return [{ name, priceCents: Math.round(euros * 100), isStudent: false, quantity: null }]
}

export function addressHiddenAllowed(details: ChatDetails): boolean {
  return hasConfirmationStep({ entry: details.entry, privacy: details.privacy })
}

/** The draft as it goes to the form, with what the chat settled. */
export function draftWithDetails(
  draft: EventDraftResponse,
  details: ChatDetails,
  description: string,
): EventDraftResponse {
  return {
    ...draft,
    description,
    startTime: details.startTime,
    endTime: details.endTime,
    location: details.location,
    listingId: details.listingId,
    locationPending: details.locationPending,
    addressHidden: addressHiddenAllowed(details) && details.addressHidden,
    ticketUrl: details.entry === 'link' && details.ticketUrl ? details.ticketUrl : null,
    ticketUrlKind: details.ticketUrlKind,
    capacity: details.capacity,
    visibility: details.privacy,
    minAge: details.minAge,
    studentsOnly: details.studentsOnly,
    targetAgeMin: details.targetAgeMin,
    targetAgeMax: details.targetAgeMax,
    audienceNote: details.audienceNote.trim() || null,
    signupRequired: details.entry === 'signup',
    tags: {
      ...draft.tags,
      mainCategoryId: details.mainCategoryId,
      subCategoryTags: details.subCategoryTags,
      amenityIds: details.amenityIds,
    },
  }
}

function nextDay(date: string): string {
  const day = new Date(`${date}T12:00`)
  day.setDate(day.getDate() + 1)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`
}

/** Start and end from a date and two clock times; an end before the start runs past midnight. */
export function scheduleFrom(
  date: string,
  start: string,
  end: string,
): Pick<ChatDetails, 'startTime' | 'endTime'> | null {
  if (!date || !start) return null
  const startTime = `${date}T${start}`
  if (!end) return { startTime, endTime: null }
  return { startTime, endTime: `${end <= start ? nextDay(date) : date}T${end}` }
}

export function splitSchedule(details: Pick<ChatDetails, 'startTime' | 'endTime'>): {
  date: string
  start: string
  end: string
} {
  const [date = '', start = ''] = (details.startTime ?? '').split('T')
  const end = (details.endTime ?? '').split('T')[1] ?? ''
  return { date, start: start.slice(0, 5), end: end.slice(0, 5) }
}
